import React, { useCallback, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { useTranslation } from 'react-i18next'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import Heading from '../../../components/Heading'
import Checkbox from '../../../components/Checkbox'
import Icon from '../../../components/Icon'
import parseStringToHTML from '../../../helpers/parseStringToHTML'
import { Article } from '../../../types/Article'
import styles from '../CustomizerPopup.module.css'

type ExtraArticleSelectorProps = {
    articles: Article[]
    selectedIds: string[]
    onChange: (selectedIds: string[]) => void
    accentColor?: string
}

const ExtraArticleSelector: React.FC<ExtraArticleSelectorProps> = ({
    articles,
    selectedIds,
    onChange,
    accentColor,
}) => {
    const { t } = useTranslation(['customizer'])
    const [openTooltipId, setOpenTooltipId] = useState<string | null>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)

    const normalizedSelectedIds = selectedIds.map((id) => String(id))

    const handleToggle = useCallback(
        (property: string, checked: boolean) => {
            if (checked) {
                onChange([...normalizedSelectedIds, property])
                return
            }

            onChange(normalizedSelectedIds.filter((id) => id !== property))
        },
        [onChange, normalizedSelectedIds]
    )

    const handleTooltipToggle = useCallback(
        (articleId: string, event: React.MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            setOpenTooltipId((current) =>
                current === articleId ? null : articleId
            )
        },
        []
    )

    useEffect(() => {
        if (openTooltipId === null) {
            return
        }

        const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
            if (
                tooltipRef.current &&
                !tooltipRef.current.contains(event.target as Node)
            ) {
                setOpenTooltipId(null)
            }
        }

        document.addEventListener('mousedown', handleOutsideClick)
        document.addEventListener('touchstart', handleOutsideClick)

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick)
            document.removeEventListener('touchstart', handleOutsideClick)
        }
    }, [openTooltipId])

    if (articles.length === 0) {
        return null
    }

    return (
        <div className={'zr-mb-3'} ref={tooltipRef}>
            <Heading level={4} classes={'zr-mb-2'}>
                {t('extraArticles.heading')}
            </Heading>
            {articles.map((article) => {
                const articleId = String(article.kArtikel)
                const description = article.description?.trim()
                const isTooltipOpen = openTooltipId === articleId

                return (
                    <div
                        key={articleId}
                        className={cx(
                            styles['c-extra-article-row'],
                            'zr-d-flex zr-align-center zr-mb-2'
                        )}
                    >
                        <Checkbox
                            property={articleId}
                            value={normalizedSelectedIds.includes(articleId)}
                            onChange={handleToggle}
                            accentColor={accentColor}
                            label={
                                <>
                                    {article.cName}{' '}
                                    {parseStringToHTML(article.price[0])}
                                </>
                            }
                        />
                        {description && (
                            <span
                                className={cx(
                                    styles['c-extra-article-info'],
                                    'zr-ml-2'
                                )}
                            >
                                <button
                                    type="button"
                                    className={styles['c-extra-article-info-button']}
                                    aria-label={t('extraArticles.heading')}
                                    aria-expanded={isTooltipOpen}
                                    onClick={(event) =>
                                        handleTooltipToggle(articleId, event)
                                    }
                                >
                                    <Icon
                                        icon={faCircleInfo}
                                        size={'1rem'}
                                    />
                                </button>
                                {isTooltipOpen && (
                                    <span
                                        className={
                                            styles['c-extra-article-tooltip']
                                        }
                                        role="tooltip"
                                    >
                                        {description}
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export default ExtraArticleSelector
